import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookConsultModal from '../components/BookConsultModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, CheckCircle, Shield, Users, Handshake, TrendingUp, Heart, Zap } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const PricingPage = () => {
  const [showModal, setShowModal] = useState(false);

  const included = [
    { id: 'inc-helpdesk', text: 'Helpdesk (email/phone)' },
    { id: 'inc-remote', text: 'Remote support' },
    { id: 'inc-monitoring', text: 'Device monitoring' },
    { id: 'inc-patching', text: 'Patching & updates' },
    { id: 'inc-o365', text: 'Microsoft 365 admin' },
    { id: 'inc-backup', text: 'Backup oversight' },
    { id: 'inc-vendor', text: 'Vendor coordination' },
    { id: 'inc-reviews', text: 'Quarterly reviews' }
  ];

  const whyPartner = [
    { id: 'why-1', icon: Handshake, title: 'True Partnership', desc: 'We invest in understanding your business. Your success is our success.' },
    { id: 'why-2', icon: TrendingUp, title: 'Aggressive Pricing', desc: 'We work hard to find a price that fits your budget and delivers real value.' },
    { id: 'why-3', icon: Shield, title: 'Long-Term Commitment', desc: 'We build relationships, not transactions. Our pricing reflects ongoing partnership.' },
    { id: 'why-4', icon: Zap, title: 'Flexible Solutions', desc: 'Every organization is different. We tailor our services and pricing to your needs.' }
  ];

  const onboarding = [
    { id: 'onb-1', title: 'Audit + Documentation', desc: 'We document your entire environment' },
    { id: 'onb-2', title: 'Security Baseline', desc: 'MFA, permissions, and policies' },
    { id: 'onb-3', title: 'Monitoring + Backups', desc: 'Set up monitoring and verify backups' },
    { id: 'onb-4', title: 'Roadmap', desc: 'Prioritized improvement plan' }
  ];

  const pricingFaqs = [
    { id: 'faq-custom', q: 'How do you determine pricing?', a: 'We take the time to understand your organization—your size, complexity, current challenges, and goals. Then we work together to arrive at a fair price that reflects the value we deliver and fits within your budget.' },
    { id: 'faq-devices', q: 'What if we have more devices than users?', a: 'Our pricing typically includes all internal network devices (switches, routers, servers, NAS, SANs, copiers, printers) regardless of quantity. We focus on supporting your people, not counting devices.' },
    { id: 'faq-nonprofit', q: 'Do you offer special pricing for non-profits?', a: 'Absolutely. We have a soft spot for mission-driven organizations. If you are a registered 501(c)(3), let us know—we will work extra hard to make the numbers work for you.' },
    { id: 'faq-commitment', q: 'What kind of commitment do you expect?', a: 'We are looking for partners, not one-time projects. Our pricing is designed for ongoing relationships where we become an extension of your team and continuously improve your technology environment.' },
    { id: 'faq-quote', q: 'How do I get a quote?', a: 'Book a free consultation with us. We will learn about your organization, discuss your needs, and put together a proposal that makes sense for both of us. No pressure, no obligation.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Pricing That Works for You</h1>
          <p className="text-xl text-gray-600 mb-6 max-w-3xl mx-auto">
            We do not believe in one-size-fits-all pricing. Every organization is unique, and your IT support should reflect that.
          </p>
          <p className="text-lg text-blue-700 font-semibold mb-8">
            We are aggressive on pricing and committed to finding a solution that fits your budget.
          </p>
          <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowModal(true)} data-testid="pricing-hero-consult">
            <Calendar className="w-5 h-5 mr-2" />
            Let&apos;s Talk About Your Needs
          </Button>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Approach to Pricing</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We are not interested in quick wins or short-term contracts. We want to be your IT partner for years to come—and our pricing reflects that commitment.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyPartner.map((item) => (
              <Card key={item.id} className="border-2 border-gray-100 hover:border-blue-200 transition-colors">
                <CardContent className="pt-6 text-center">
                  <div className="w-14 h-14 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Tiers - No Prices */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Services We Offer</h2>
            <p className="text-lg text-gray-600">Comprehensive IT support tailored to your organization</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Managed IT */}
            <Card className="border-2 border-blue-500 shadow-2xl relative">
              <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white">Most Popular</Badge>
              <CardHeader className="text-center bg-gradient-to-br from-blue-50 to-cyan-50">
                <CardTitle className="text-2xl mb-4">Managed IT Support</CardTitle>
                <p className="text-gray-600">Complete IT management for small businesses and nonprofits</p>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Support for all user devices</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700"><strong>24/7 availability</strong> when you need us</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Network infrastructure management</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Server management & monitoring</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Microsoft 365 administration</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Security baseline & policies</span>
                  </li>
                </ul>
                <div className="text-center mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">Pricing based on your specific needs</p>
                </div>
                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => setShowModal(true)} data-testid="pricing-managed-consult">
                  <Calendar className="w-4 h-4 mr-2" />
                  Discuss Your Needs
                </Button>
              </CardContent>
            </Card>

            {/* Non-Profit Focus */}
            <Card className="border-2 border-green-500 shadow-xl relative">
              <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-green-600 text-white">Non-Profit Friendly</Badge>
              <CardHeader className="text-center bg-gradient-to-br from-green-50 to-emerald-50">
                <div className="w-12 h-12 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
                  <Heart className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle className="text-2xl mb-4">Non-Profit IT Support</CardTitle>
                <p className="text-gray-600">Special consideration for mission-driven organizations</p>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">All managed IT features included</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Budget-conscious pricing approach</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Microsoft 365 nonprofit licensing assistance</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Grant-friendly billing options</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Understanding of nonprofit challenges</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Flexible terms for tight budgets</span>
                  </li>
                </ul>
                <div className="text-center mb-4 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800 font-medium">We work hard to fit your budget</p>
                </div>
                <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => setShowModal(true)} data-testid="pricing-nonprofit-consult">
                  Talk to Us About Your Mission
                </Button>
              </CardContent>
            </Card>

            {/* Security Assessment */}
            <Card className="border-2 border-purple-500 shadow-xl relative">
              <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white">Security Focus</Badge>
              <CardHeader className="text-center bg-gradient-to-br from-purple-50 to-pink-50">
                <div className="w-12 h-12 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-3">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-2xl mb-4">Cybersecurity Assessment</CardTitle>
                <p className="text-gray-600">Comprehensive security evaluation for your organization</p>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="font-semibold text-gray-900 mb-3">What you receive:</p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Account + MFA review</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Device security baseline</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Microsoft 365 security check</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Backup and recovery review</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Network exposure analysis</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Risk report + remediation plan</span>
                  </li>
                </ul>
                <div className="text-center mb-4 p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-800 font-medium">Scope-based pricing discussed upfront</p>
                </div>
                <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={() => setShowModal(true)} data-testid="pricing-security-consult">
                  Get a Security Assessment
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">What&apos;s Included in Managed IT</h2>
          <p className="text-lg text-gray-600 text-center mb-12">Everything you need, nothing you do not</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {included.map((item) => (
              <div key={item.id} className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">{item.text}</span>
              </div>
            ))}
          </div>

          <Card className="max-w-4xl mx-auto mt-12 border-2 border-blue-100 bg-blue-50/50">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-4">
                <Users className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">We Focus on People, Not Devices</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our pricing is centered around supporting your team members—not counting every device in your office. All your infrastructure (servers, switches, routers, printers) is included. We want to make IT simple and predictable for you.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Month 1 Onboarding */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">How We Get Started</h2>
          <p className="text-lg text-gray-600 text-center mb-12">A structured onboarding process to set you up for success</p>
          <div className="grid md:grid-cols-4 gap-8">
            {onboarding.map((step, index) => (
              <Card key={step.id} className="border border-gray-200 text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 mx-auto bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-xl font-bold text-white">{index + 1}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Common Questions</h2>
          <div className="space-y-6">
            {pricingFaqs.map((faq) => (
              <Card key={faq.id} className="border border-gray-200 hover:border-blue-200 transition-colors">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.q}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Talk?</h2>
          <p className="text-xl text-blue-50 mb-4">
            Let&apos;s have a conversation about your organization and find a solution that works.
          </p>
          <p className="text-lg text-blue-100 mb-8">
            No pressure. No obligation. Just an honest discussion about how we can help.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" onClick={() => setShowModal(true)} data-testid="pricing-cta-consult">
              <Calendar className="w-5 h-5 mr-2" />
              Book a Free Consult
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
              774-644-7101
            </Button>
          </div>
          <p className="text-sm text-blue-100 mt-6">
            We respond within 1 business day (often faster). Emergency support available.
          </p>
        </div>
      </section>

      <Footer />
      <BookConsultModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default PricingPage;
